import moment from 'moment';
import React, { useCallback } from 'react';
import { IoMdDownload } from 'react-icons/io';
import { useParams } from 'react-router-dom';
import { ActionIcon, Text, Tooltip } from 'rizzui';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import CustomTable from '../../../../../components/table/CustomTable';
import useHandleDownloadDocument from '../../../../../hooks/useDownloadDocument';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import { DocumentsService } from '../../../../../services/document/documentsService';
import { OrderService } from '../../../../../services/order/orderService';
import OrderProfileLayout from '../../OrderProfileLayout';

const xmlHistoryColumnOrder = [
    'id',
    'originalName',
    'xmlType',
    'user',
    'createdAt',
    'extension',
];

const OrderXmlHistory: React.FC = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { handleDownloadDocument } = useHandleDownloadDocument();
    const documentsService = new DocumentsService();
    const {
        filters,
        updateFilterOrder,
        updatePage,
        updatePageSize,
    } = useFilters({ orderId: id }, [{ field: 'createdAt', order: 'desc' }], 1, 25, { orderId: id });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [data, loading] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await (new OrderService()).listXmlExportHistory(filters);
        return response.getResponseData();
    }, [id, filters]));

    const orderedData = () => data?.data?.map((row: any) => {
        const formattedRow: any = {
            id: row?.document?.id,
            originalName: row?.document?.originalName,
            xmlType: row?.xmlType === 'end' ? 'XML fin' : 'XML inicio',
            user: row?.user,
            createdAt: row?.createdAt?.date ? moment(row.createdAt.date).format('DD/MM/YYYY HH:mm') : '',
            extension: row?.document?.extension,
        };

        const orderedRow: any = {};
        xmlHistoryColumnOrder.forEach((column) => {
            orderedRow[column] = formattedRow[column];
        });

        return orderedRow;
    });

    return (
        <OrderProfileLayout>
            {data !== undefined
                ? (
                    <div>
                        <Text className='mb-4 text-lg font-semibold'>Histórico XML del pedido</Text>

                        <CustomTable
                            id={'orders-xml-history-table'}
                            columnOrder={xmlHistoryColumnOrder}
                            data={orderedData() || []}
                            isLoading={loading}
                            columnsNotShown={['id', 'extension']}
                            filters={filters}
                            updateFilterOrder={updateFilterOrder}
                            defaultOrder={filters.filter_order || undefined}
                            paginationData={{
                                pageSize: filters.limit,
                                currentPage: filters.page,
                                pageCount: data ? data.lastPage : 1,
                                totalCount: data?.totalRegisters,
                                handlePagination: updatePage,
                                handlePerPage: updatePageSize,
                            }}
                            search={false}
                            filter={false}
                            viewFilters={false}
                            overrideColumns={[
                                {
                                    key: 'user',
                                    render: (row: any) => {
                                        const user = row.row.original?.user;
                                        if (!user) {
                                            return <Text className='text-sm text-gray-500'>Sistema</Text>;
                                        }

                                        return (
                                            <Text className='text-sm text-gray-700'>
                                                {[user.name, user.lastName].filter(Boolean).join(' ')}
                                            </Text>
                                        );
                                    }
                                },
                            ]}
                            actions={[
                                {
                                    label: 'Descargar',
                                    render: (row: any) => (
                                        <Tooltip
                                            size='sm'
                                            content={'Descargar XML'}
                                            placement='top'
                                            color='invert'
                                        >
                                            <ActionIcon
                                                as='span'
                                                size='sm'
                                                variant='outline'
                                                className='hover:!border-gray-900 hover:text-gray-700 cursor-pointer'
                                                onClick={() => handleDownloadDocument(documentsService.downloadDocument, row.id, row.originalName)}
                                            >
                                                <IoMdDownload className='h-4 w-4' size={18} />
                                            </ActionIcon>
                                        </Tooltip>
                                    ),
                                },
                            ]}
                        />
                    </div>
                )
                : <Loader height="70vh" />
            }
        </OrderProfileLayout>
    );
};

export default OrderXmlHistory;
